import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Search, Edit2, Trash2, Eye, MoreVertical, FileText } from 'lucide-react';
import axios from 'axios';
import AdminLayout from '@/components/AdminLayout';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminBlogs = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();

  const fetchBlogs = async () => {
    try {
      const response = await axios.get(`${API}/blogs`, { headers: getAuthHeaders() });
      setBlogs(response.data);
    } catch (error) {
      console.error('Failed to fetch blogs:', error);
      toast.error('Failed to load blogs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBlogs();
  }, []);

  const handleDelete = async () => {
    if (!deleteId) return;
    
    setDeleting(true);
    try {
      await axios.delete(`${API}/blogs/${deleteId}`, { headers: getAuthHeaders() });
      toast.success('Blog post deleted successfully');
      fetchBlogs();
    } catch (error) {
      console.error('Failed to delete blog:', error);
      toast.error('Failed to delete blog post');
    } finally {
      setDeleting(false);
      setDeleteId(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const filteredBlogs = blogs.filter(blog => 
    blog.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    blog.category_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <AdminLayout>
      <div className="page-transition" data-testid="admin-blogs">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h1 className="font-['Manrope'] text-2xl md:text-3xl font-bold text-slate-900 mb-2">
              Blog Posts
            </h1>
            <p className="text-slate-500">Manage your blog content</p>
          </div>
          <Link to="/admin/blogs/new">
            <Button className="btn-primary rounded-full text-white" data-testid="create-blog-btn">
              <Plus className="w-5 h-5 mr-2" />
              New Post
            </Button>
          </Link>
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              type="text"
              placeholder="Search posts..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 h-12 rounded-xl search-input"
              data-testid="search-input"
            />
          </div>
        </div>

        {/* Blog List */}
        <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
          {loading ? (
            <div className="p-8 space-y-4">
              {Array(5).fill(0).map((_, i) => (
                <div key={i} className="flex items-center gap-4">
                  <div className="skeleton h-16 w-16 rounded-xl"></div>
                  <div className="flex-1">
                    <div className="skeleton h-5 w-48 mb-2 rounded"></div>
                    <div className="skeleton h-4 w-32 rounded"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : filteredBlogs.length === 0 ? (
            <div className="p-12 text-center">
              <FileText className="w-16 h-16 text-slate-200 mx-auto mb-4" />
              <h3 className="font-['Manrope'] text-lg font-semibold text-slate-900 mb-2">
                {searchQuery ? 'No posts found' : 'No blog posts yet'}
              </h3>
              <p className="text-slate-500 mb-6">
                {searchQuery 
                  ? 'Try a different search term'
                  : 'Create your first blog post to get started'}
              </p>
              {!searchQuery && (
                <Link to="/admin/blogs/new">
                  <Button className="btn-primary rounded-full text-white">
                    <Plus className="w-5 h-5 mr-2" />
                    Create Post
                  </Button>
                </Link>
              )}
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {filteredBlogs.map((blog) => (
                <div
                  key={blog.id}
                  className="flex items-center gap-4 p-4 md:p-6 admin-table-row"
                  data-testid={`blog-row-${blog.id}`}
                >
                  {/* Thumbnail */}
                  <div className="w-16 h-16 md:w-20 md:h-20 rounded-xl bg-gradient-to-br from-[#6F42F0]/10 to-[#10B981]/10 flex items-center justify-center flex-shrink-0 overflow-hidden">
                    {blog.featured_image ? (
                      <img 
                        src={blog.featured_image.startsWith('/api') 
                          ? `${process.env.REACT_APP_BACKEND_URL}${blog.featured_image}` 
                          : blog.featured_image}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <FileText className="w-6 h-6 text-[#6F42F0]" />
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-['Manrope'] font-semibold text-slate-900 truncate mb-1">
                      {blog.title}
                    </h3>
                    <div className="flex flex-wrap items-center gap-2 text-sm text-slate-500">
                      <span>{formatDate(blog.created_at)}</span>
                      {blog.category_name && (
                        <>
                          <span>•</span>
                          <span>{blog.category_name}</span>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Status */}
                  <span className={`hidden md:inline-flex px-3 py-1 rounded-full text-xs font-medium ${
                    blog.status === 'published' 
                      ? 'status-published' 
                      : 'status-draft'
                  }`}>
                    {blog.status}
                  </span>

                  {/* Actions */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-10 w-10"
                        data-testid={`blog-actions-${blog.id}`}
                      >
                        <MoreVertical className="w-5 h-5 text-slate-400" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48">
                      <DropdownMenuItem 
                        onClick={() => navigate(`/admin/blogs/${blog.id}`)}
                        className="cursor-pointer"
                      >
                        <Edit2 className="w-4 h-4 mr-2" />
                        Edit
                      </DropdownMenuItem>
                      {blog.status === 'published' && (
                        <DropdownMenuItem 
                          onClick={() => window.open(`/blog/${blog.slug}`, '_blank')}
                          className="cursor-pointer"
                        >
                          <Eye className="w-4 h-4 mr-2" />
                          View
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem 
                        onClick={() => setDeleteId(blog.id)}
                        className="cursor-pointer text-red-600 focus:text-red-600 focus:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Blog Post</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete this blog post? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                disabled={deleting}
                className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AdminLayout>
  );
};

export default AdminBlogs;
